<script setup>
import { usePlatformApi } from '@/utils/platformApi'
import { requiredValidator } from '@core/utils/validators'

definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()

const platformAccessToken = useCookie('platformAccessToken')

if (!platformAccessToken.value)
  await router.push('/platform/login')

const form = ref({
  title: '',
  slug: '',
  excerpt: '',
  content: '',
})

const formRef = ref()
const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

// Sama pola dengan platform/tenants/create.vue -- auto-generate slug dari
// judul selama admin belum mengedit slug-nya sendiri secara manual.
// Keunikan tetap ditangani backend (BlogPostService::uniqueSlug()), ini
// murni preview supaya admin tahu alamat /blog/{slug} yang akan dipakai.
const slugTouched = ref(false)

watch(() => form.value.title, title => {
  if (slugTouched.value)
    return

  form.value.slug = title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
})

// ─── Gambar utama ───────────────────────────────────────────────────────────
// Reuse pola upload yang sudah ada (catalog/products/create.vue): artikel
// dibuat dulu (butuh id), baru gambar di-upload ke endpoint terpisah --
// TIDAK bikin komponen/mekanisme upload baru, cuma VFileInput + FormData
// biasa lewat usePlatformApi (sudah otomatis menangani FormData & header
// Authorization platform, lihat utils/platformApi.js).
const imageFile = ref(null)
const imagePreview = ref(null)
const uploadingImage = ref(false)

const handleImageChange = file => {
  if (!file) {
    imagePreview.value = null
    imageFile.value = null

    return
  }
  imageFile.value = file
  imagePreview.value = URL.createObjectURL(file)
}

const submit = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid)
    return

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const res = await usePlatformApi('/api/v1/platform/blog-posts').post({ body: form.value })
    const post = res?.data ?? res
    const postId = post?.id

    if (imageFile.value && postId) {
      uploadingImage.value = true

      const fd = new FormData()

      fd.append('image', imageFile.value)

      try {
        await usePlatformApi(`/api/v1/platform/blog-posts/${postId}/image`).post({ body: fd })
      } catch (uploadErr) {
        console.warn('[Platform] upload gambar artikel gagal', uploadErr)
      } finally {
        uploadingImage.value = false
      }
    }

    successMessage.value = res?.message ?? 'Artikel berhasil dibuat sebagai draft.'

    setTimeout(() => router.push('/platform/blog-posts'), 800)
  } catch (err) {
    const status = err?.status ?? err?.response?.status
    const data = err?.data ?? err?.response?._data

    if (status === 422 && data?.errors)
      errors.value = data.errors
    else
      errors.value = { _general: [data?.message ?? 'Gagal membuat artikel.'] }
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-3">
      <h4 class="text-h4">
        Tulis Artikel Baru
      </h4>
    </div>

    <VForm
      ref="formRef"
      @submit.prevent="submit"
    >
      <VRow>
        <VCol
          cols="12"
          md="8"
        >
          <VCard>
            <VCardText class="pa-5">
              <VAlert
                v-if="successMessage"
                type="success"
                variant="tonal"
                class="mb-4"
              >
                {{ successMessage }}
              </VAlert>

              <VAlert
                v-if="errors._general"
                type="error"
                variant="tonal"
                class="mb-4"
              >
                {{ errors._general[0] }}
              </VAlert>

              <VTextField
                v-model="form.title"
                label="Judul Artikel *"
                class="mb-4"
                :rules="[requiredValidator]"
                :error-messages="errors.title"
              />

              <VTextField
                v-model="form.slug"
                label="Slug (URL)"
                prefix="/blog/"
                hint="Otomatis dari judul, bisa diedit manual. Kalau sudah dipakai artikel lain, sistem otomatis menambahkan angka di belakangnya."
                persistent-hint
                class="mb-4"
                :error-messages="errors.slug"
                @update:model-value="slugTouched = true"
              />

              <VTextarea
                v-model="form.excerpt"
                label="Ringkasan (opsional)"
                rows="2"
                hint="Cuplikan singkat yang tampil di daftar /blog. Kosongkan kalau tidak perlu."
                persistent-hint
                class="mb-4"
                :error-messages="errors.excerpt"
              />

              <VTextarea
                v-model="form.content"
                label="Isi Artikel *"
                rows="16"
                :rules="[requiredValidator]"
                :error-messages="errors.content"
              />
            </VCardText>
          </VCard>
        </VCol>

        <VCol
          cols="12"
          md="4"
        >
          <VCard class="mb-4">
            <VCardTitle class="pt-4 px-4 text-body-1 font-weight-bold">
              Gambar Utama
            </VCardTitle>
            <VCardText class="pa-4">
              <div
                class="image-upload-area rounded-lg d-flex flex-column align-center justify-center pa-4 mb-3"
                :class="imagePreview ? '' : 'border-dashed'"
              >
                <VImg
                  v-if="imagePreview"
                  :src="imagePreview"
                  height="140"
                  cover
                  rounded="lg"
                />
                <div
                  v-else
                  class="text-center"
                >
                  <VIcon
                    icon="ri-image-add-line"
                    size="40"
                    color="medium-emphasis"
                  />
                  <p class="text-caption text-medium-emphasis mt-2 mb-0">
                    Klik untuk unggah gambar
                  </p>
                </div>
              </div>
              <VFileInput
                label="Pilih Gambar"
                accept="image/*"
                hide-details
                prepend-icon=""
                prepend-inner-icon="ri-upload-line"
                variant="outlined"
                :loading="uploadingImage"
                @update:model-value="handleImageChange"
              />
              <p class="text-caption text-medium-emphasis mt-1 mb-0">
                Maks. 2MB · JPG, PNG
              </p>
            </VCardText>
          </VCard>

          <VBtn
            type="submit"
            color="primary"
            block
            size="large"
            :loading="loading"
          >
            Simpan sebagai Draft
          </VBtn>
          <p class="text-caption text-medium-emphasis mt-2 mb-0 text-center">
            Artikel baru selalu tersimpan sebagai draft dulu -- publish lewat halaman daftar artikel.
          </p>
        </VCol>
      </VRow>
    </VForm>
  </div>
</template>

<style scoped>
.image-upload-area {
  min-block-size: 140px;
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.border-dashed {
  border: 2px dashed rgba(var(--v-border-color), var(--v-border-opacity));
}
</style>
